import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { fetchTeamMembers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';

interface CommissionSplit {
  userId: string;
  splitPercentage: number;
}

interface CommissionSplitEditorProps {
  splits: CommissionSplit[];
  onChange: (splits: CommissionSplit[]) => void;
  feeAmount: number;
}

export function CommissionSplitEditor({
  splits,
  onChange,
  feeAmount,
}: CommissionSplitEditorProps) {
  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team-members'],
    queryFn: fetchTeamMembers,
  });

  const totalPercentage = splits.reduce((sum, s) => sum + s.splitPercentage, 0);
  const isValid = totalPercentage <= 100 + 0.01;

  const handleAddSplit = () => {
    onChange([...splits, { userId: '', splitPercentage: 0 }]);
  };

  const handleRemoveSplit = (index: number) => {
    const updated = splits.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleUserChange = (index: number, userId: string) => {
    const updated = splits.map((s, i) => (i === index ? { ...s, userId } : s));
    onChange(updated);
  };

  const handlePercentageChange = (index: number, value: string) => {
    const percentage = parseFloat(value) || 0;
    const updated = splits.map((s, i) =>
      i === index ? { ...s, splitPercentage: Math.min(100, Math.max(0, percentage)) } : s,
    );
    onChange(updated);
  };

  const calculateAmount = (percentage: number): number => {
    return (feeAmount * percentage) / 100;
  };

  // Determine which user IDs are already selected so they can be filtered from other dropdowns
  const selectedUserIds = new Set(splits.map((s) => s.userId).filter(Boolean));

  return (
    <div className="space-y-3">
      {splits.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Team Member</TableHead>
              <TableHead className="w-[120px]">Split %</TableHead>
              <TableHead className="w-[140px] text-right">Amount</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {splits.map((split, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Select
                    value={split.userId}
                    onValueChange={(value) => handleUserChange(index, value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((member) => (
                        <SelectItem
                          key={member.id}
                          value={member.id}
                          disabled={selectedUserIds.has(member.id) && member.id !== split.userId}
                        >
                          {member.firstName} {member.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={split.splitPercentage || ''}
                      onChange={(e) => handlePercentageChange(index, e.target.value)}
                      className="pr-6"
                      placeholder="0"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-text-tertiary text-sm">
                      %
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatCurrency(calculateAmount(split.splitPercentage), 'ZAR')}
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-text-tertiary hover:text-red-500"
                    onClick={() => handleRemoveSplit(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-medium">Total</TableCell>
              <TableCell
                className={`font-semibold ${
                  isValid ? 'text-emerald-500' : 'text-red-500'
                }`}
              >
                {totalPercentage.toFixed(1)}%
              </TableCell>
              <TableCell className="text-right font-semibold">
                {formatCurrency(
                  splits.reduce((sum, s) => sum + calculateAmount(s.splitPercentage), 0),
                  'ZAR',
                )}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-text-tertiary border border-dashed border-white/10 rounded-lg">
          <p className="text-sm">No commission splits added</p>
        </div>
      )}

      {!isValid && splits.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-red-500">
          <AlertCircle className="h-4 w-4" />
          <span>
            Total split percentage is {totalPercentage.toFixed(1)}% (must not exceed 100%)
          </span>
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAddSplit}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Split
      </Button>
    </div>
  );
}
